// src/components/profile-input/FormFields.jsx
import React, { useState } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
  Chip,
  Button,
  IconButton,
  Grid,
  FormHelperText,
  InputAdornment,
  Autocomplete,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Typography,
  Divider
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Flag as FlagIcon,
  Clear as ClearIcon,
  WhatsApp as WhatsAppIcon
} from "@mui/icons-material";
import { options } from "./formUtils";

// Text Input with validation
export const TextInput = ({ 
  label, 
  value, 
  onChange, 
  required = false, 
  helperText = "",
  type = "text",
  error = false,
  disabled = false,
  placeholder = "",
  multiline = false,
  rows = 1,
  fullWidth = true,
  variant = "outlined",
  margin = "normal",
  size = "medium",
  startAdornment,
  endAdornment,
  ...props 
}) => (
  <TextField
    fullWidth={fullWidth}
    label={label}
    value={value}
    onChange={onChange}
    required={required}
    helperText={helperText}
    type={type}
    error={error}
    disabled={disabled}
    placeholder={placeholder}
    multiline={multiline}
    rows={rows}
    variant={variant}
    margin={margin}
    size={size}
    InputProps={{
      startAdornment: startAdornment && (
        <InputAdornment position="start">{startAdornment}</InputAdornment>
      ),
      endAdornment: endAdornment && (
        <InputAdornment position="end">{endAdornment}</InputAdornment>
      ),
    }}
    {...props}
  />
);

// Select Input with search
export const SelectInput = ({ 
  label, 
  value, 
  onChange, 
  options = [], 
  required = false, 
  error = false,
  helperText = "",
  disabled = false,
  fullWidth = true,
  variant = "outlined",
  margin = "normal",
  size = "medium",
  displayEmpty = true,
  ...props 
}) => (
  <FormControl 
    fullWidth={fullWidth} 
    margin={margin} 
    error={error}
    disabled={disabled}
    variant={variant}
    size={size}
  >
    <InputLabel>{label}{required && " *"}</InputLabel>
    <Select
      value={value}
      onChange={onChange}
      label={label}
      required={required}
      displayEmpty={displayEmpty}
      {...props}
    >
      {displayEmpty && <MenuItem value=""><em>Select {label}</em></MenuItem>}
      {options.map((option) => (
        <MenuItem key={option} value={option}>{option}</MenuItem>
      ))}
    </Select>
    {helperText && <FormHelperText>{helperText}</FormHelperText>}
  </FormControl>
);

// Multi-Select Input
export const MultiSelectInput = ({ 
  label, 
  value = [], 
  onChange, 
  options = [], 
  error = false,
  helperText = "",
  disabled = false,
  fullWidth = true,
  limitTags = 3,
  ...props 
}) => (
  <FormControl fullWidth={fullWidth} error={error} disabled={disabled}>
    <InputLabel>{label}</InputLabel>
    <Select
      multiple
      value={value}
      onChange={onChange}
      input={<OutlinedInput label={label} />}
      renderValue={(selected) => {
        if (selected.length === 0) {
          return <em>Select {label}</em>;
        }
        if (selected.length > limitTags) {
          return `${selected.slice(0, limitTags).join(', ')} +${selected.length - limitTags} more`;
        }
        return selected.join(', ');
      }}
      {...props}
    >
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          <Checkbox checked={value.indexOf(option) > -1} />
          <ListItemText primary={option} />
        </MenuItem>
      ))}
    </Select>
    {helperText && <FormHelperText>{helperText}</FormHelperText>}
  </FormControl>
);

// Country Code Selector with flags
export const CountryCodeInput = ({ 
  value, 
  onChange, 
  label = "Country Code",
  error = false,
  helperText = "",
  disabled = false,
  fullWidth = true,
  ...props 
}) => (
  <FormControl fullWidth={fullWidth} error={error} disabled={disabled}>
    <InputLabel>{label}</InputLabel>
    <Select
      value={value}
      onChange={onChange}
      label={label}
      startAdornment={
        <InputAdornment position="start">
          <FlagIcon fontSize="small" />
        </InputAdornment>
      }
      {...props}
    >
      {options.country_codes.map((country) => (
        <MenuItem key={country.code} value={country.code}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography component="span" sx={{ fontSize: '1.2em' }}>
              {country.flag}
            </Typography>
            <Typography component="span">
              {country.code} ({country.country})
            </Typography>
          </Box>
        </MenuItem>
      ))}
    </Select>
    {helperText && <FormHelperText>{helperText}</FormHelperText>}
  </FormControl>
);

// Phone Input with country code
export const PhoneInput = ({ 
  label, 
  phoneValue, 
  onPhoneChange, 
  countryCodeValue, 
  onCountryCodeChange,
  required = false,
  error = false,
  helperText = "",
  disabled = false,
  showWhatsApp = false,
  onWhatsAppToggle,
  whatsappEnabled = false,
  ...props 
}) => {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1 }}>
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <CountryCodeInput
              value={countryCodeValue}
              onChange={onCountryCodeChange}
              disabled={disabled}
              size="small"
            />
          </Grid>
          <Grid item xs={8}>
            <TextInput
              label={label}
              value={phoneValue}
              onChange={onPhoneChange}
              required={required}
              error={error}
              helperText={helperText}
              disabled={disabled}
              type="tel"
              startAdornment={<PhoneIcon fontSize="small" />}
              size="small"
              {...props}
            />
          </Grid>
        </Grid>
      </Box>
      {showWhatsApp && (
        <IconButton 
          onClick={onWhatsAppToggle}
          color={whatsappEnabled ? "success" : "default"}
          disabled={disabled}
          sx={{ mt: 1 }}
          title={whatsappEnabled ? "WhatsApp Enabled" : "Enable WhatsApp"}
        >
          <WhatsAppIcon />
        </IconButton>
      )}
    </Box>
  );
};

// Array Input for dynamic lists
export const ArrayInput = ({ 
  label, 
  value = [], 
  onAdd, 
  onRemove, 
  inputValue, 
  onInputChange,
  placeholder = "Add item...",
  helperText = "",
  error = false,
  disabled = false,
  maxItems,
  ...props 
}) => {
  const [input, setInput] = useState(inputValue || "");

  const handleAdd = () => {
    if (input.trim() && (maxItems === undefined || value.length < maxItems)) {
      onAdd(input.trim());
      setInput("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <TextField
          fullWidth
          label={label}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            onInputChange && onInputChange(e.target.value);
          }}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          disabled={disabled || (maxItems !== undefined && value.length >= maxItems)}
          error={error}
          helperText={helperText || (maxItems && `${value.length}/${maxItems} items`)}
          size="small"
          {...props}
        />
        <Button
          variant="outlined"
          onClick={handleAdd}
          startIcon={<AddIcon />}
          disabled={!input.trim() || disabled || (maxItems !== undefined && value.length >= maxItems)}
          size="small"
        >
          Add
        </Button>
      </Box>
      {value.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          {value.map((item, index) => (
            <Chip
              key={index}
              label={item}
              onDelete={() => onRemove(index)}
              color="primary"
              variant="outlined"
              size="small"
              deleteIcon={<ClearIcon />}
              disabled={disabled}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// Range Input for min-max values
export const RangeInput = ({ 
  label,
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  minLabel = "Min",
  maxLabel = "Max",
  required = false,
  error = false,
  helperText = "",
  disabled = false,
  type = "number",
  ...props 
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle2" gutterBottom>
      {label}{required && " *"}
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <TextInput
          label={minLabel}
          value={minValue}
          onChange={onMinChange}
          type={type}
          error={error}
          disabled={disabled}
          fullWidth
          size="small"
          {...props}
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          label={maxLabel}
          value={maxValue}
          onChange={onMaxChange}
          type={type}
          error={error}
          disabled={disabled}
          fullWidth
          size="small"
          {...props}
        />
      </Grid>
    </Grid>
    {helperText && (
      <FormHelperText error={error}>{helperText}</FormHelperText>
    )}
  </Box>
);

// Date Input with validation
export const DateInput = ({
  label,
  value,
  onChange,
  required = false,
  error = false,
  helperText = "",
  disabled = false,
  maxDate,
  minDate,
  ...props
}) => {
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    // Convert DD-MM-YYYY to YYYY-MM-DD for input type="date"
    if (dateStr.includes('-')) {
      const [day, month, year] = dateStr.split('-');
      return `${year}-${month}-${day}`;
    }
    return dateStr;
  };

  const handleChange = (e) => {
    const dateValue = e.target.value;
    if (dateValue) {
      // Convert YYYY-MM-DD to DD-MM-YYYY
      const [year, month, day] = dateValue.split('-');
      onChange(`${day}-${month}-${year}`);
    } else {
      onChange("");
    }
  };

  return (
    <TextInput
      label={label}
      value={formatDateForInput(value)}
      onChange={handleChange}
      required={required}
      error={error}
      helperText={helperText}
      disabled={disabled}
      type="date"
      InputLabelProps={{ shrink: true }}
      inputProps={{
        max: maxDate,
        min: minDate
      }}
      fullWidth
      {...props}
    />
  );
};

// Currency Input with selector
export const CurrencyInput = ({
  label,
  amountValue,
  onAmountChange,
  currencyValue,
  onCurrencyChange,
  required = false,
  error = false,
  helperText = "",
  disabled = false,
  ...props
}) => {
  return (
    <Box>
      <Grid container spacing={1}>
        <Grid item xs={8}>
          <TextInput
            label={label}
            value={amountValue}
            onChange={onAmountChange}
            required={required}
            error={error}
            helperText={helperText}
            disabled={disabled}
            type="number"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography color="textSecondary">
                    {options.currencies.find(c => c.value === currencyValue)?.symbol || "₨"}
                  </Typography>
                </InputAdornment>
              ),
            }}
            {...props}
          />
        </Grid>
        <Grid item xs={4}>
          <SelectInput
            value={currencyValue}
            onChange={onCurrencyChange}
            options={options.currencies.map(c => c.value)}
            disabled={disabled}
            size="small"
            sx={{ mt: 0.5 }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// Height Input with dual units
export const HeightInput = ({
  label = "Height",
  cmValue,
  onCmChange,
  feetValue,
  inchesValue,
  onFeetChange,
  onInchesChange,
  required = false,
  error = false,
  helperText = "",
  disabled = false,
  showBothUnits = true,
  ...props
}) => {
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}{required && " *"}
      </Typography>
      
      {showBothUnits ? (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextInput
              label="Centimeters (cm)"
              value={cmValue}
              onChange={onCmChange}
              type="number"
              error={error}
              disabled={disabled}
              fullWidth
              size="small"
              InputProps={{
                endAdornment: <InputAdornment position="end">cm</InputAdornment>,
              }}
              {...props}
            />
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextInput
                label="Feet"
                value={feetValue}
                onChange={onFeetChange}
                type="number"
                error={error}
                disabled={disabled}
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">ft</InputAdornment>,
                }}
                {...props}
              />
              <TextInput
                label="Inches"
                value={inchesValue}
                onChange={onInchesChange}
                type="number"
                error={error}
                disabled={disabled}
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">in</InputAdornment>,
                }}
                {...props}
              />
            </Box>
          </Grid>
        </Grid>
      ) : (
        <TextInput
          label="Height (cm)"
          value={cmValue}
          onChange={onCmChange}
          type="number"
          required={required}
          error={error}
          helperText={helperText}
          disabled={disabled}
          fullWidth
          InputProps={{
            endAdornment: <InputAdornment position="end">cm</InputAdornment>,
          }}
          {...props}
        />
      )}
      {helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </Box>
  );
};

// Section Header
export const SectionHeader = ({ 
  title, 
  subtitle = "", 
  divider = true,
  ...props 
}) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" gutterBottom {...props}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {subtitle}
      </Typography>
    )}
    {divider && <Divider sx={{ mt: 1 }} />}
  </Box>
);

// Info Text
export const InfoText = ({ 
  children, 
  icon,
  ...props 
}) => (
  <Box sx={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: 1,
    p: 1.5,
    bgcolor: 'background.default',
    borderRadius: 1,
    mb: 2
  }}>
    {icon}
    <Typography variant="body2" color="textSecondary" {...props}>
      {children}
    </Typography>
  </Box>
);